import * as React from "react";
import * as SwitchPrimitives from "@radix-ui/react-switch";
import { cn } from "../../utils/classMerger";

export type TwoSidedSwitchSide = "left" | "right";

interface TwoSidedSwitchProps {
    id: string;
    value: TwoSidedSwitchSide;
    leftLabel: string;
    rightLabel: string;
    onChange: (value: TwoSidedSwitchSide) => void;
    disabled?: boolean;
    className?: string;
}

// Two-sided switch: both labels stay visible and the thumb sits on the active
// side, so neither position has to be decoded as on/off. The track keeps one
// colour for the same reason - neither side is the "enabled" one.
const TwoSidedSwitch = React.forwardRef<
    React.ElementRef<typeof SwitchPrimitives.Root>,
    TwoSidedSwitchProps
>(
    (
        {
            id,
            value,
            leftLabel,
            rightLabel,
            onChange,
            disabled = false,
            className,
        },
        ref,
    ) => {
        const isRight = value === "right";

        // The words are mouse targets that pick their own side; the switch itself
        // carries the accessible name and keyboard handling (Space / Enter).
        const sideLabel = (
            side: TwoSidedSwitchSide,
            text: string,
        ): React.ReactElement => (
            <button
                type="button"
                tabIndex={-1}
                aria-hidden
                disabled={disabled}
                onClick={() => onChange(side)}
                className={cn(
                    "px-1 py-1 text-xs transition-colors",
                    value === side
                        ? "font-semibold text-primary"
                        : "font-medium text-gray-400 hover:text-gray-600",
                    disabled && "cursor-not-allowed opacity-50",
                )}
            >
                {text}
            </button>
        );

        return (
            <div className={cn("flex items-center gap-1", className)}>
                {sideLabel("left", leftLabel)}
                <SwitchPrimitives.Root
                    ref={ref}
                    id={id}
                    aria-label={rightLabel}
                    checked={isRight}
                    disabled={disabled}
                    onCheckedChange={(checked) =>
                        onChange(checked ? "right" : "left")
                    }
                    className="inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent bg-primary shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    <SwitchPrimitives.Thumb className="pointer-events-none block h-4 w-4 rounded-full bg-white shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0" />
                </SwitchPrimitives.Root>
                {sideLabel("right", rightLabel)}
            </div>
        );
    },
);
TwoSidedSwitch.displayName = "TwoSidedSwitch";

export default TwoSidedSwitch;
